import { useId, useRef, useState, type ReactNode } from "react";
import { T, useT } from "../i18n";
import { PfeilLinks } from "../schale/Kopf";
import { ATTRAPPE } from "../daten/klient";
import { Fassung } from "../bausteine/Fassung";
import { Faktorbild } from "../bausteine/Faktorbild";
import type { Anmeldung as Angemeldet } from "../daten/typen";
import { Anmeldesperre, MINDESTLAENGE, useAnmeldelauf, usePasswortwechsel } from "./anmeldelauf";
import "../stile/telefon/anmeldung.css";
import { LogoBild } from "../schale/LogoBild";

/* ══════════════════════════════════════════════════════════════════
   Die Anmeldung am Telefon — eine eigene Maske (19.09.2026).

   Luca: *„Das Mobile-Login sieht wirklich scheiße aus. Da muss ein
   komplett separater Login-Screen her, der komplett optimiert ist."*

   ── Was vorher da war ─────────────────────────────────────────────

   Die Maske des Schreibtischs, untereinander gestapelt: links die Marke
   auf dem Trecker, rechts das Formular — am Telefon oben ein Bildstreifen
   mit Logo, drei Säulen und Fassung, darunter das Formular in
   Schreibtischmaß, der Knopf irgendwo in der Mitte, und wenn die Tastatur
   aufging, lag er darunter.

   ── Was jetzt gilt ────────────────────────────────────────────────

   **Ein Schirm, gebaut wie eine App:**

   | Teil | was er tut |
   |---|---|
   | Kopf | das Logo von Machine Office, mittig, mit Luft darum — bei offener Tastatur kleiner, damit der Platz den Feldern gehört (bis zum Abend des 19.09. stand hier der Trecker als Bild) |
   | Formular | Titel mittig in Saira, Felder 56 px mit 17-px-Schrift (unter 16 zoomt Safari beim Hineintippen) |
   | Fuß | der gelbe Knopf über die ganze Breite, **unten, wo der Daumen ist** — und bei offener Tastatur über ihr (`schale/tastatur.ts`) |

   **Die Tastatur ist Teil der Gestaltung.** `type`, `inputMode`,
   `autoComplete` und `enterKeyHint` stehen an jedem Feld so, dass der
   Schlüsselbund E-Mail und Passwort einsetzt, die Taste unten rechts
   „Weiter" bzw. „Los" heißt und der Code aus der Passwortverwaltung über
   der Tastatur angeboten wird (`one-time-code`).

   **Kein Fokus von selbst — mit einer Ausnahme.** Wer die App öffnet,
   soll erst sehen, wo er ist; die Tastatur kommt auf seinen Tipp. Nur der
   Code-Schritt setzt den Fokus: Er kommt ausschließlich nach „Anmelden",
   und der Code wird erst angeboten, wenn das Feld den Fokus hat
   (`anmeldelauf.tsx`).

   **Fehler sind ein Satz unter dem Feld**, in Rot und ruhig — kein Band
   über der Maske, das man wegdrücken muss.

   **Was hier nicht steht:** „Anderes Verfahren wählen" — es gibt nur die
   App, und ein Knopf, der nichts tut, wäre eine Falle. „Passwort vergessen?"
   steht seit Signal (07.10.2026) da, aber als Verweis, der sagt, wer
   weiterhilft — einen Mailweg gibt es noch nicht.

   **Signal** (docs/64, Paket 2): oben das Band in der Farbe der Hülle mit dem
   vollen Logo, darüber das helle Blatt (`stile/telefon/anmeldung.css`).
   ══════════════════════════════════════════════════════════════════ */

/** Der Rahmen jedes Schritts: Logo, Titel und Felder, Fuß mit der Handlung. */
function Rahmen({ zurueck, titel, unter, children, fuss }: {
  zurueck?: { tun: () => void; aus?: boolean } | undefined;
  titel: string;
  unter?: string | undefined;
  children: ReactNode;
  fuss: ReactNode;
}) {
  const t = useT();
  return (
    <div className="anm-t">
      {/* **Kein Bild, nur das Logo** (19.09.2026, abends). Luca: *„Ich will
          dieses Bild gar nicht im Mobile-Login haben. Das soll einfach ein
          cleaner, schicker Mobile-Login sein mit dem Logo von Machine
          Office."* Zwei Bilder, eines je Palette sichtbar — dieselbe Regel
          wie in der Kopfleiste (`stile/schale.css`). */}
      <header className="anm-t-held">
        <LogoBild className="anm-t-logo logo-hell" art="hell" alt={t("app.logoAlt")} />
        <LogoBild className="anm-t-logo logo-dunkel" art="dunkel" alt="" />
      </header>
      <div className="anm-t-blatt">
        <main className="anm-t-leib">
          <div className="anm-t-inhalt">
            {zurueck ? (
              <button
                type="button"
                className="anm-t-zurueck"
                onClick={zurueck.tun}
                disabled={zurueck.aus}
                aria-label={t("allgemein.zurueck")}
                title={t("allgemein.zurueck")}
              >
                <PfeilLinks />
              </button>
            ) : null}
            <h1 className="anm-t-titel">{titel}</h1>
            {unter ? <p className="anm-t-unter">{unter}</p> : null}
            {children}
          </div>
        </main>
        <footer className="anm-t-fuss">{fuss}</footer>
      </div>
    </div>
  );
}

/** Ein Feld: Beschriftung darüber, das Eingabefeld 56 px, darunter der Fehler. */
function Feld({ id, schild, children, fehler, hinweis }: {
  id: string;
  schild: string;
  children: ReactNode;
  fehler?: string | null | undefined;
  hinweis?: string | undefined;
}) {
  return (
    <div className="anm-t-feld">
      <label className="anm-t-schild" htmlFor={id}>{schild}</label>
      {children}
      {fehler ? <p className="anm-t-fehler" role="alert">{fehler}</p>
        : hinweis ? <p className="anm-t-hinweis">{hinweis}</p> : null}
    </div>
  );
}

/**
 * Ein Passwortfeld mit „Zeigen". Auf dem Telefon tippt man blind auf einer
 * kleinen Tastatur — wer sehen kann, was er geschrieben hat, braucht einen
 * Versuch weniger. Der Knopf steht im Feld und ist 44 px hoch.
 */
function Passwortfeld({ id, wert, setzen, autoComplete, zeigen, umschalten, enterKeyHint, feld, ungueltig }: {
  id: string;
  wert: string;
  setzen: (w: string) => void;
  autoComplete: "current-password" | "new-password";
  zeigen: boolean;
  umschalten: () => void;
  enterKeyHint: "go" | "next" | "done";
  feld?: React.Ref<HTMLInputElement>;
  ungueltig?: boolean;
}) {
  const t = useT();
  return (
    <div className="anm-t-eingabe mit-knopf">
      <input
        ref={feld}
        id={id}
        className="anm-t-input"
        type={zeigen ? "text" : "password"}
        autoComplete={autoComplete}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint={enterKeyHint}
        value={wert}
        onChange={(e) => setzen(e.target.value)}
        aria-invalid={ungueltig || undefined}
      />
      <button
        type="button"
        className="anm-t-zeigen"
        aria-pressed={zeigen}
        aria-label={t(zeigen ? "anmeldung.passwortVerbergenSchild" : "anmeldung.passwortZeigenSchild")}
        onClick={umschalten}
      >
        {t(zeigen ? "anmeldung.passwortVerbergen" : "anmeldung.passwortZeigen")}
      </button>
    </div>
  );
}

/** Der Hauptknopf im Fuß — gelb, über die ganze Breite. */
function Hauptknopf({ form, laeuft, beiKlick, children }: {
  form?: string;
  laeuft: boolean;
  beiKlick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      className="anm-t-knopf"
      type={form ? "submit" : "button"}
      form={form}
      disabled={laeuft}
      aria-busy={laeuft}
      onClick={beiKlick}
    >
      {children}
    </button>
  );
}

/** Der fällige Passwortwechsel — am Telefon ein Schritt, kein Fenster darüber. */
function WechselTelefon({ mail, altes, abbrechen, drin }: {
  mail: string;
  altes: string;
  abbrechen: () => void;
  drin: (antwort: Angemeldet) => void;
}) {
  const t = useT();
  const id = useId();
  const [zeigen, setzeZeigen] = useState(false);
  const { neu, setzeNeu, nochmal, setzeNochmal, fehler, laeuft, speichern } =
    usePasswortwechsel({ mail, altes, drin });
  return (
    <Rahmen
      zurueck={{ tun: abbrechen, aus: laeuft }}
      titel={t("anmeldung.wechselTitel")}
      unter={t("anmeldung.wechselUnter")}
      fuss={(
        <>
          <Hauptknopf form={`${id}-form`} laeuft={laeuft}>{t("anmeldung.wechselKnopf")}</Hauptknopf>
          <button type="button" className="anm-t-leise" disabled={laeuft} onClick={abbrechen}>
            {t("anmeldung.wechselAbbrechen")}
          </button>
        </>
      )}
    >
      <form
        id={`${id}-form`}
        className="anm-t-formular"
        onSubmit={(e) => { e.preventDefault(); void speichern(); }}
      >
        {/* Für Passwortverwalter: Zu welchem Konto das neue gehört. */}
        <input type="email" autoComplete="username" value={mail} readOnly hidden />
        <Feld id={`${id}-neu`} schild={t("anmeldung.wechselNeu")} hinweis={t("anmeldung.wechselHinweis", { n: MINDESTLAENGE })}>
          <Passwortfeld
            id={`${id}-neu`} wert={neu} setzen={setzeNeu} autoComplete="new-password"
            zeigen={zeigen} umschalten={() => setzeZeigen((z) => !z)} enterKeyHint="next"
          />
        </Feld>
        <Feld id={`${id}-nochmal`} schild={t("anmeldung.wechselNochmal")} fehler={fehler}>
          <Passwortfeld
            id={`${id}-nochmal`} wert={nochmal} setzen={setzeNochmal} autoComplete="new-password"
            zeigen={zeigen} umschalten={() => setzeZeigen((z) => !z)} enterKeyHint="go"
          />
        </Feld>
      </form>
    </Rahmen>
  );
}

export function AnmeldungTelefon() {
  const t = useT();
  const id = useId();
  const lauf = useAnmeldelauf();
  const [zeigen, setzeZeigen] = useState(false);
  const [vergessen, setzeVergessen] = useState(false);
  const wortFeld = useRef<HTMLInputElement>(null);

  if (lauf.wechsel) {
    return (
      <WechselTelefon
        mail={lauf.mail}
        altes={lauf.wort}
        abbrechen={() => lauf.setzeWechsel(false)}
        drin={lauf.gewechselt}
      />
    );
  }

  /* ── Der Code ──
     Ein Feld für alle sechs Ziffern, darüber sechs gezeichnete Kästen. Das
     Feld liegt unsichtbar über den Kästen: Getippt, eingefügt und vom
     Telefon eingesetzt wird in **ein** echtes Eingabefeld — sechs Felder,
     zwischen denen der Fokus springt, verlieren beim Einsetzen auf manchem
     Telefon fünf Ziffern. Die Kästen zeigen nur, was darin steht. */
  if (lauf.stufe === "code") {
    const code = lauf.ziffern.join("");
    return (
      <Rahmen
        zurueck={{ tun: lauf.zurueckZurAnmeldung, aus: lauf.laeuft }}
        titel={t(lauf.einrichtung ? "faktor.einrichtenTitel" : "anmeldung.codeTitel")}
        unter={t(lauf.einrichtung ? "faktor.einrichtenUnter" : "anmeldung.codeUnter")}
        fuss={(
          <Hauptknopf laeuft={lauf.laeuft} beiKlick={() => lauf.codePruefen(code)}>
            {t("anmeldung.knopf")}
          </Hauptknopf>
        )}
      >
        {/* Noch kein Faktor, aber verlangt (E4): erst das Bild, dann der Code. */}
        {lauf.einrichtung ? <Faktorbild e={lauf.einrichtung} /> : null}
        <div className="anm-t-feld">
          <label className="anm-t-schild" htmlFor={`${id}-code`}>{t("anmeldung.codeSchild")}</label>
          <div className="anm-t-code" data-falsch={lauf.falsch ? "ja" : undefined}>
            {lauf.ziffern.map((z, i) => (
              <span
                key={i}
                className="anm-t-ziffer"
                data-voll={z ? "ja" : undefined}
                data-dran={i === Math.min(code.length, 5) ? "ja" : undefined}
                aria-hidden="true"
              >
                {z}
              </span>
            ))}
            <input
              ref={(el) => { lauf.felder.current[0] = el; }}
              id={`${id}-code`}
              className="anm-t-code-feld"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              enterKeyHint="go"
              maxLength={6}
              value={code}
              onChange={(e) => lauf.codeSetzen(e.target.value)}
              /* Nach einem abgelehnten Code stehen sechs Ziffern darin, und
                 mehr nimmt das Feld nicht an. Beim Hineintippen ist alles
                 markiert — die nächste Ziffer fängt neu an, statt dass man
                 sechsmal löscht. */
              onFocus={(e) => e.currentTarget.select()}
            />
          </div>
          {lauf.fehler ? <p className="anm-t-fehler" role="alert">{lauf.fehler}</p> : null}
        </div>
        {ATTRAPPE ? <p className="anm-t-tipp"><T k="anmeldung.codeTipp" /></p> : null}
      </Rahmen>
    );
  }

  /* ── E-Mail und Passwort ── */
  return (
    <Rahmen
      /* „Willkommen" und nicht „Anmelden": Das Wort steht schon auf dem Knopf
         darunter, und zweimal dasselbe auf einem Schirm ist eines zu viel. */
      titel={t("anmeldung.telefonTitel")}
      unter={t("anmeldung.unter")}
      fuss={(
        <>
          <Hauptknopf form={`${id}-form`} laeuft={lauf.laeuft}>{t("anmeldung.knopf")}</Hauptknopf>
          <p className="anm-t-hausregel">{t("anmeldung.keinKonto")}</p>
          <p className="anm-t-fassung"><Fassung /></p>
        </>
      )}
    >
      {/* Ohne Netz kommt hier niemand hinein — und was aufgenommen wurde,
          liegt auf dem Gerät (`anmeldelauf.tsx`, `Anmeldesperre`). */}
      <Anmeldesperre />
      {lauf.hinweis ? <p className="netzband" role="status"><span>{lauf.hinweis}</span></p> : null}
      <form
        id={`${id}-form`}
        className="anm-t-formular"
        onSubmit={(e) => { e.preventDefault(); void lauf.anmeldenVersuchen(); }}
      >
        {/* Fehlt die E-Mail, steht der Satz unter ihr — nicht unter dem Passwort (A-01). */}
        <Feld id={`${id}-mail`} schild={t("anmeldung.mail")} fehler={lauf.leer === "mail" ? lauf.fehler : null}>
          <div className="anm-t-eingabe">
            {/* „Weiter" auf der Tastatur führt ins Passwort und schickt nicht
                ein halbes Formular ab. */}
            <input
              id={`${id}-mail`}
              className="anm-t-input"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              value={lauf.mail}
              onChange={(e) => lauf.setzeMail(e.target.value)}
              aria-invalid={lauf.leer === "mail" || undefined}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                wortFeld.current?.focus();
              }}
            />
          </div>
        </Feld>
        <Feld id={`${id}-wort`} schild={t("anmeldung.passwort")} fehler={lauf.leer === "mail" ? null : lauf.fehler}>
          <Passwortfeld
            id={`${id}-wort`} wert={lauf.wort} setzen={lauf.setzeWort} autoComplete="current-password"
            zeigen={zeigen} umschalten={() => setzeZeigen((z) => !z)} enterKeyHint="go" feld={wortFeld}
            ungueltig={lauf.leer === "wort"}
          />
        </Feld>
        {/* **„Passwort vergessen?"** (07.10.2026, Signal): bis hierher stand
            es am Telefon gar nicht. Einen Mailweg gibt es noch nicht — der
            Verweis sagt, wer weiterhilft (docs/47). */}
        <button
          type="button"
          className="anm-t-verweis"
          aria-expanded={vergessen}
          aria-controls={`${id}-vergessen`}
          onClick={() => setzeVergessen((x) => !x)}
        >
          {t("anmeldung.passwortVergessen")}
        </button>
        {vergessen ? (
          <p className="anm-t-wissen" id={`${id}-vergessen`} role="status">{t("anmeldung.passwortVergessenSatz")}</p>
        ) : null}
        <label className="anm-t-merken">
          <input
            type="checkbox"
            checked={lauf.merken}
            onChange={(e) => lauf.setzeMerken(e.target.checked)}
          />
          <span className="anm-t-haken" aria-hidden="true" />
          <span>{t("anmeldung.merken")}</span>
        </label>
      </form>
    </Rahmen>
  );
}
